import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { useHelpStore } from "@/lib/store/help.store";

type AppType = "dentlab" | "dentconnect";

interface Release {
	id: string;
	created_at: string;
	app: string | null;
	title: string | null;
	summary: string | null;
	changelog_slug: string | null;
}

const PAGE_SIZE = 20;

export const useReleaseNotes = (app: AppType) => {
	const [releases, setReleases] = useState<Release[]>([]);
	const [readIds, setReadIds] = useState<Set<string>>(new Set());
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const profileIdRef = useRef<string | null>(null);
	const initializedRef = useRef(false);
	const readIdsRef = useRef(readIds);
	readIdsRef.current = readIds;

	const fetchReadIds = useCallback(async (): Promise<Set<string>> => {
		const { data: session } = await supabase.auth.getSession();
		const userId = session.session?.user.id;
		if (!userId) return new Set();
		profileIdRef.current = userId;

		const { data, error } = await supabase
			.from("attributes_profile")
			.select("key")
			.eq("profile_id", userId)
			.like("key", "release_read_%");

		if (error) {
			Logger.error("Failed to fetch release read status: " + String(error));
			return new Set();
		}

		const ids = new Set(
			(data ?? []).map((a) => a.key.replace("release_read_", ""))
		);
		setReadIds(ids);
		return ids;
	}, []);

	const fetchReleases = useCallback(
		async (offset = 0): Promise<Release[]> => {
			setLoading(true);
			const { data, error } = await (supabase
				.from("releases")
				.select("*") as any)
				.eq("app", app)
				.not("changelog_slug", "is", null)
				.order("created_at", { ascending: false })
				.range(offset, offset + PAGE_SIZE - 1);

			setLoading(false);

			if (error) {
				Logger.error("Failed to fetch releases: " + String(error));
				return [];
			}

			const fetched = (data ?? []) as Release[];
			setHasMore(fetched.length === PAGE_SIZE);

			if (offset === 0) {
				setReleases(fetched);
			} else {
				setReleases((prev) => [...prev, ...fetched]);
			}

			return fetched;
		},
		[app]
	);

	const initialize = useCallback(async () => {
		if (initializedRef.current) return;
		initializedRef.current = true;

		const [fetchedReadIds, fetchedReleases] = await Promise.all([
			fetchReadIds(),
			fetchReleases(0),
		]);

		const initialUnreadCount = fetchedReleases.filter(
			(r) => !fetchedReadIds.has(r.id)
		).length;

		const store = useHelpStore.getState();
		store.setUnreadReleaseCount(initialUnreadCount);
		if (initialUnreadCount > 0) {
			store.openReleases();
		}
	}, [fetchReadIds, fetchReleases]);

	useEffect(() => {
		initialize();
	}, [initialize]);

	const loadMore = useCallback(async () => {
		if (loading || !hasMore) return;
		const fetched = await fetchReleases(releases.length);
		const newUnreads = fetched.filter(
			(r) => !readIdsRef.current.has(r.id)
		).length;
		if (newUnreads > 0) {
			const store = useHelpStore.getState();
			store.setUnreadReleaseCount(store.unreadReleaseCount + newUnreads);
		}
	}, [loading, hasMore, releases.length, fetchReleases]);

	const markAsRead = useCallback(
		async (releaseId: string) => {
			if (readIds.has(releaseId)) return;

			setReadIds((prev) => new Set(prev).add(releaseId));

			const newUnread = releases.filter(
				(r) => !readIds.has(r.id) && r.id !== releaseId
			).length;
			useHelpStore.getState().setUnreadReleaseCount(newUnread);

			const profileId = profileIdRef.current;
			if (!profileId) return;

			const { error } = await supabase
				.from("attributes_profile")
				.upsert(
					{
						profile_id: profileId,
						key: `release_read_${releaseId}`,
						value: 1,
					},
					{ onConflict: "profile_id,key" }
				);

			if (error) {
				Logger.error("Failed to mark release as read: " + String(error));
			}
		},
		[readIds, releases]
	);

	const markAllAsRead = useCallback(async () => {
		const unread = releases.filter((r) => !readIds.has(r.id));
		if (unread.length === 0) return;

		setReadIds((prev) => {
			const next = new Set(prev);
			for (const r of unread) next.add(r.id);
			return next;
		});

		useHelpStore.getState().setUnreadReleaseCount(0);

		const profileId = profileIdRef.current;
		if (!profileId) return;

		const rows = unread.map((r) => ({
			profile_id: profileId,
			key: `release_read_${r.id}`,
			value: 1,
		}));

		const { error } = await supabase
			.from("attributes_profile")
			.upsert(rows, { onConflict: "profile_id,key" });

		if (error) {
			Logger.error("Failed to mark all releases as read: " + String(error));
		}
	}, [releases, readIds]);

	const unreadCount = releases.filter((r) => !readIds.has(r.id)).length;

	return {
		releases,
		unreadCount,
		loading,
		hasMore,
		readIds,
		markAsRead,
		markAllAsRead,
		loadMore,
	};
};
